/**
 * What: Shared compact segmented navigation for Shopify embedded app status filters.
 * Transport / data-flow: Pure browser React component over Polaris buttons; callers own filtering state.
 *
 */
import { Button, ButtonGroup } from '@shopify/polaris'
import type { MouseEvent } from 'react'
import './ShopifySegmentedFilterNav.css'

export type ShopifySegmentedFilterOption<FilterId extends string> = {
  id: FilterId
  label: string
  disabled?: boolean
}

type ShopifySegmentedFilterNavProps<FilterId extends string> = {
  filters: readonly ShopifySegmentedFilterOption<FilterId>[]
  selected: FilterId
  onSelect: (filterId: FilterId) => void
  equalHeight?: boolean
}

function variantForFilter<FilterId extends string>(current: FilterId, target: FilterId) {
  if (current === target) return 'primary' as const
  return 'secondary' as const
}

/** Keeps compact lifecycle/status controls consistent across merchant workflow pages. */
export function ShopifySegmentedFilterNav<FilterId extends string>({
  filters,
  selected,
  onSelect,
  equalHeight = false,
}: ShopifySegmentedFilterNavProps<FilterId>) {
  return (
    <div className={`tf-segmented-filter-nav${equalHeight ? ' tf-segmented-filter-nav--equal-height' : ''}`}>
      <ButtonGroup variant="segmented">
        {filters.map((filter) => (
          <Button
            key={filter.id}
            variant={variantForFilter(selected, filter.id)}
            disabled={filter.disabled}
            onClick={() => onSelect(filter.id)}
          >
            {filter.label}
          </Button>
        ))}
      </ButtonGroup>
    </div>
  )
}

/** Native links for a server-selected result choice. The caller supplies every URL and selected flag. */
export function ShopifySegmentedFilterLinks({
  choices,
  onNavigate,
  pending = false,
}: {
  choices: readonly { id: string; label: string; selected: boolean; href: string }[]
  onNavigate?: (event: MouseEvent<HTMLDivElement>, href: string) => void
  pending?: boolean
}) {
  const navigate = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element)) return
    const anchor = event.target.closest('a')
    if (anchor) onNavigate?.(event, anchor.href)
  }
  return (
    <div className="tf-segmented-filter-nav" onClickCapture={navigate}>
      <ButtonGroup variant="segmented">
        {choices.map((choice) => (
          <Button
            key={choice.id}
            variant={variantForSelection(choice.selected)}
            pressed={choice.selected}
            url={choiceUrl(choice, pending)}
          >
            {choice.label}
          </Button>
        ))}
      </ButtonGroup>
    </div>
  )
}
function choiceUrl(choice: { selected: boolean; href: string }, pending: boolean) {
  if (choice.selected && !pending) return undefined
  return choice.href
}
function variantForSelection(selected: boolean) {
  if (selected) return 'primary' as const
  return 'secondary' as const
}
